<template>
  <div>
    <b-card>
      <b-overlay :show="formShow" rounded="sm" no-fade>
        <div class="billing-tabs" role="tablist" aria-label="Customer type">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            role="tab"
            class="billing-tab"
            :class="{ active: activeTab === tab.value }"
            :aria-selected="activeTab === tab.value"
            @click="switchTab(tab.value)"
          >
            <span class="billing-tab-icon"><feather-icon :icon="tab.icon" size="18" /></span>
            <span class="billing-tab-text">
              <span class="billing-tab-label">{{ tab.label }}</span>
              <span class="billing-tab-sub">{{ tab.sub }}</span>
            </span>
            <span
              v-if="tabCounts[tab.value] !== null"
              class="billing-tab-count"
              :class="{ attention: activeTab !== tab.value && tabCounts[tab.value] > 0 }"
              :aria-label="`${tabCounts[tab.value]} records`"
            >{{ tabCounts[tab.value] }}</span>
          </button>
        </div>

        <b-card class="mt-2">
          <div v-if="currentRoute === 'billing-history'" class="history-switch mb-2" role="tablist">
            <button
              v-for="view in historyViews"
              :key="view.value"
              type="button"
              role="tab"
              :aria-selected="historyView === view.value"
              :class="{ active: historyView === view.value }"
              @click="switchHistoryView(view.value)"
            >{{ view.label }}</button>
          </div>

          <b-alert :show="pending.count > 0" variant="warning" class="pending-alert">
            <div class="alert-body d-flex align-items-center flex-wrap">
              <feather-icon icon="AlertTriangleIcon" class="mr-50" />
              <span>
                There {{ pending.count === 1 ? 'is' : 'are' }} <b>{{ pending.count }}</b> pending {{ pending.count === 1 ? 'record' : 'records' }}
                between <b>{{ formatDate(pending.from) }}</b> and <b>{{ formatDate(pending.to) }}</b>.
              </span>
              <b-button variant="flat-warning" size="sm" class="ml-auto" @click="showPending">Show them</b-button>
            </div>
          </b-alert>

          <b-row>
            <template v-if="usesDates">
              <b-col cols="12" xl="2">
                <h6 class="mb-1">Start Date</h6>
                <flat-pickr v-model="startDate" style="width:100%; background-color:white" :config="startConfig" class="form-control invoice-edit-input" />
              </b-col>

              <b-col cols="12" xl="2">
                <h6 class="mb-1">End Date</h6>
                <flat-pickr v-model="endDate" style="width:100%; background-color:white" :config="endConfig" class="form-control invoice-edit-input" />
              </b-col>
            </template>
            <b-col v-else cols="12" xl="4" class="d-flex align-items-end">
              <p class="billing-hint mb-50">{{ showingDismissed ? 'Showing every dismissed record, newest first.' : 'Showing every record waiting for approval.' }}</p>
            </b-col>

            <b-col cols="12" xl="3" class="d-flex align-items-end">
              <b-form-checkbox v-model="isNavMenuHidden" name="is-vertical-menu-collapsed" class="mb-50" inline>Collapse Menu</b-form-checkbox>
            </b-col>

            <b-col cols="12" xl="5" class="d-flex align-items-end justify-content-xl-end mt-1 mt-xl-0">
              <b-button variant="outline-primary" class="mr-1" :disabled="!invoiceData.length" @click="exportExcel">
                Export
              </b-button>
              <b-button v-if="currentRoute === 'billing-approval'" variant="outline-warning" class="mr-1" :disabled="!selectedRows.length" @click="runReject">
                <feather-icon icon="CornerUpLeftIcon" size="14" class="mr-25" />Reject {{ selectedRows.length || '' }}
              </b-button>
              <b-button v-if="canDismiss" variant="outline-danger" class="mr-1" :disabled="!selectedRows.length" @click="runDismiss">
                Dismiss {{ selectedRows.length || '' }}
              </b-button>
              <b-button v-if="action" variant="primary" :disabled="!selectedRows.length" @click="runAction">
                {{ action.label }} {{ selectedRows.length }} {{ selectedRows.length === 1 ? 'row' : 'rows' }} ({{ formatPrice(selectedTotal) }})
              </b-button>
            </b-col>
          </b-row>

          <div v-if="usesDates" class="billing-cycles mt-2">
            <span class="billing-cycles-label">Select start date or end date or select billing cycle:</span>
            <div class="cycle-year">
              <button type="button" class="cycle-year-btn" aria-label="Previous year" @click="cycleYear--">
                <feather-icon icon="ChevronLeftIcon" size="14" />
              </button>
              <span>{{ cycleYear }}</span>
              <button type="button" class="cycle-year-btn" aria-label="Next year" :disabled="cycleYear >= currentYear" @click="cycleYear++">
                <feather-icon icon="ChevronRightIcon" size="14" />
              </button>
            </div>
            <div class="cycle-months">
              <button
                v-for="month in cycleMonths"
                :key="month.index"
                type="button"
                class="cycle-chip"
                :class="{ active: month.active }"
                :disabled="month.future"
                @click="selectCycle(month.index)"
              >{{ month.name }}</button>
            </div>
          </div>

          <p v-if="editable" class="billing-hint mt-2 mb-0">
            <feather-icon icon="Edit2Icon" size="12" /> Double-click a highlighted cell to edit it. Labor amounts (Dr) are calculated from the labor rate × invoiced hours.
          </p>

          <div class="billing-table-wrap mt-1">
            <b-table
              hover
              small
              show-empty
              empty-text="No matching records found"
              :items="invoiceData"
              :fields="tableFields"
              :tbody-tr-class="rowClass"
              thead-class="green-bg text-white"
            >
              <!-- Selection -->
              <template #head(selected)>
                <b-form-checkbox :checked="allSelected" :indeterminate="someSelected" aria-label="Select all rows" @change="toggleAll" />
              </template>
              <template #cell(selected)="{ item }">
                <b-form-checkbox v-model="item.selected" aria-label="Select row" />
              </template>

              <!-- Header help -->
              <template v-for="(tip, key) in headerTips" v-slot:[`head(${key})`]="{ label }">
                <span :key="key" v-b-tooltip.hover.top="tip" class="th-help">{{ label }} <feather-icon icon="InfoIcon" size="11" /></span>
              </template>

              <!-- Money -->
              <template #cell(Dr)="{ item }">
                <span class="text-nowrap font-weight-bold">{{ formatPrice(item.Dr) }}</span>
              </template>

              <!-- Worked hours, flagged when over the quote -->
              <template #cell(HrsWorked)="{ item }">
                <span
                  v-if="isOverQuote(item)"
                  v-b-tooltip.hover.top="`Worked ${formatHours(item.HrsWorked)}h vs ${formatHours(item.HrsQuoted)}h quoted`"
                  class="over-quote"
                >
                  <feather-icon icon="AlertTriangleIcon" size="12" /> {{ formatHours(item.HrsWorked) }}
                </span>
                <span v-else>{{ formatHours(item.HrsWorked) }}</span>
              </template>

              <template #cell(HrsQuoted)="{ item }">{{ formatHours(item.HrsQuoted) }}</template>

              <template #cell(Project)="{ item }">
                <span class="text-nowrap">{{ item.Project }}</span>
                <span
                  v-if="item.RejectedBy || item.RejectedAt"
                  v-b-tooltip.hover.top="rejectionTip(item)"
                  class="rejected-tag"
                >Rejected</span>
              </template>

              <template #cell(DismissedAt)="{ item }">
                <span class="text-nowrap">{{ formatDateTime(item.DismissedAt) }}</span>
              </template>

              <template #cell(PreviousStatus)="{ item }">
                {{ item.PreviousStatus === 'Confirmation' ? 'Finalize' : 'Create' }}
              </template>

              <template #cell(isPartiallyInvoiced)="{ item }">
                <b-form-checkbox v-if="editable && isLabor(item)" v-model="item.isPartiallyInvoiced" switch @change="onPartialCheckboxChange(item)" />
                <span v-else>{{ item.isPartiallyInvoiced ? 'Yes' : '' }}</span>
                <small v-if="item.isPartiallyInvoiced && item.HrsRemaining > 0" class="d-block text-muted text-nowrap">{{ formatHours(item.HrsRemaining) }}h left</small>
              </template>

              <!-- Editable / text cells -->
              <template v-for="field in textFields" v-slot:[`cell(${field.key})`]="{ item }">
                <div
                  :key="field.key"
                  :class="{ 'editable-cell': canEdit(item, field.key) }"
                  @dblclick="startEditing(item, field.key)"
                >
                  <b-input
                    v-if="editingItem === item && editingCell === field.key"
                    v-model="editValue"
                    size="sm"
                    autofocus
                    :type="field.key === 'HrsInvoiced' ? 'number' : 'text'"
                    :min="field.key === 'HrsInvoiced' ? 0 : null"
                    step="0.25"
                    @blur="endEditing(item, field.key)"
                    @keyup.enter="$event.target.blur()"
                    @keyup.esc="cancelEditing"
                  />
                  <span
                    v-else
                    v-b-tooltip.hover.top="isLong(item[field.key], field) ? String(item[field.key]) : ''"
                    class="cell-text"
                    :style="{ maxWidth: (field.maxWidth || 160) + 'px' }"
                  >{{ displayValue(item[field.key], field.key) }}</span>
                </div>
              </template>

              <!-- Totals -->
              <template #custom-foot="{ fields }">
                <b-tr v-if="invoiceData.length" class="totals-row">
                  <b-td v-for="(field, i) in fields" :key="field.key" :class="field.class">
                    <template v-if="field.key === 'Dr'">{{ formatPrice(totals.Dr) }}</template>
                    <template v-else-if="['HrsQuoted', 'HrsWorked', 'HrsInvoiced'].includes(field.key)">{{ formatHours(totals[field.key]) }}</template>
                    <template v-else-if="i === (fields[0].key === 'selected' ? 1 : 0)">Total ({{ invoiceData.length }})</template>
                  </b-td>
                </b-tr>
              </template>
            </b-table>
          </div>
        </b-card>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { onUnmounted } from '@vue/composition-api';
import store from '@/store';
import { BRow, BCol, BCard, BButton, BFormCheckbox, BTable, BTr, BTd, BOverlay, BFormInput, BAlert, VBTooltip } from 'bootstrap-vue';
import flatPickr from 'vue-flatpickr-component';
import billingStoreModule from './billingStoreModule';
import router from '@/router';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useAppCustomizer from '@core/layouts/components/app-customizer/useAppCustomizer';

const CHARGE_FIELDS = ['Tub', 'Org', 'Object', 'Fund', 'Activity', 'Subactivity', 'Root'];

const ACTIONS = {
  'billing-create': { label: 'Save', verb: 'save', done: 'saved', store: 'billing/saveBilling' },
  'billing-approval': { label: 'Approve', verb: 'approve', done: 'approved', store: 'billing/approveBilling' },
  'billing-confirmation': { label: 'Finalize', verb: 'finalize', done: 'finalized', store: 'billing/finalizeBilling' },
};

const RESTORE_ACTION = { label: 'Restore', verb: 'restore', done: 'restored', store: 'billing/restoreBilling' };

const LOADERS = {
  'billing-create': 'billing/getNonInvoicedData',
  'billing-approval': 'billing/getApprovalBilling',
  'billing-confirmation': 'billing/getFinalBilling',
  'billing-history': 'billing/getFinalizedBilling',
};

function ymd(date) {
  return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function validYmd(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? value : null;
}

function parseYmd(value) {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

const col = (key, label, extra = {}) => ({ key, label, sortable: false, class: 'text-center', ...extra });

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormCheckbox,
    BTable,
    BTr,
    BTd,
    BOverlay,
    BAlert,
    BInput: BFormInput,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },

  setup() {
    const { isNavMenuHidden } = useAppCustomizer();
    const BILLING_STORE_MODULE_NAME = 'billing';

    if (!store.hasModule(BILLING_STORE_MODULE_NAME)) store.registerModule(BILLING_STORE_MODULE_NAME, billingStoreModule);

    onUnmounted(() => {
      if (store.hasModule(BILLING_STORE_MODULE_NAME)) store.unregisterModule(BILLING_STORE_MODULE_NAME);
    });

    return { isNavMenuHidden };
  },

  data() {
    const now = new Date();
    const query = (this.$route && this.$route.query) || {};
    // A link can open a specific range (?start=YYYY-MM-DD&end=YYYY-MM-DD, e.g. from the
    // monthly pending billing email); otherwise the default range is last month.
    const startDate = validYmd(query.start) || ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    const endDate = validYmd(query.end) || ymd(new Date(now.getFullYear(), now.getMonth(), 0));

    return {
      startDate,
      endDate,
      activeTab: 'internal',
      tabs: [
        { value: 'internal', label: 'Internal', sub: 'Harvard charge strings', icon: 'HomeIcon' },
        { value: 'external', label: 'External', sub: 'PO numbers', icon: 'GlobeIcon' },
      ],
      tabCounts: { internal: null, external: null },
      currentRoute: null,
      editingCell: null,
      editingItem: null,
      editValue: null,
      invoiceData: [],
      formShow: false,
      dirty: false,
      revertingDate: false,
      pending: { count: 0, from: null, to: null },
      historyView: 'finalized',
      historyViews: [
        { value: 'finalized', label: 'Finalized' },
        { value: 'dismissed', label: 'Dismissed' },
      ],
      dismissedFields: [
        col('Project', 'Project', { class: 'text-left' }),
        col('Description', 'Description', { maxWidth: 160 }),
        col('Dr', 'Dr', { class: 'text-right' }),
        col('HrsInvoiced', 'Hours'),
        col('Contact', 'Contact', { maxWidth: 140 }),
        col('PreviousStatus', 'Dismissed from'),
        col('DismissedBy', 'Dismissed by', { maxWidth: 140 }),
        col('DismissedAt', 'Dismissed on'),
        col('DismissReason', 'Reason', { class: 'text-left', maxWidth: 240 }),
      ],
      currentYear: now.getFullYear(),
      cycleYear: parseYmd(startDate).getFullYear(),

      internalFields: [
        col('Project', 'Project', { class: 'text-left' }),
        ...CHARGE_FIELDS.map((key) => col(key, key, { editable: true, maxWidth: 70 })),
        col('Dr', 'Dr', { class: 'text-right' }),
        col('Dept', 'Dept', { editable: true, maxWidth: 140 }),
        col('Faculty', 'Faculty', { editable: true, maxWidth: 120 }),
        col('Contact', 'Contact', { editable: true, maxWidth: 140 }),
        col('Detail', 'Detail', { editable: true, maxWidth: 180 }),
        col('Description', 'Description', { maxWidth: 160 }),
        col('HrsQuoted', 'Quoted'),
        col('HrsWorked', 'Worked'),
        col('HrsInvoiced', 'Invoiced', { editable: true }),
        col('isPartiallyInvoiced', 'Partial'),
        col('Notes', 'Notes', { class: 'text-left', editable: true, maxWidth: 180 }),
        col('Approver', 'Approver', { class: 'text-left', editable: true, maxWidth: 180 }),
      ],

      externalFields: [
        col('Project', 'Project', { class: 'text-left' }),
        col('PoNumber', 'PO Number', { editable: true, maxWidth: 260 }),
        col('Dr', 'Dr', { class: 'text-right' }),
        col('Dept', 'Dept', { editable: true, maxWidth: 140 }),
        col('Faculty', 'Faculty', { editable: true, maxWidth: 120 }),
        col('Contact', 'Contact', { editable: true, maxWidth: 140 }),
        col('Detail', 'Detail', { editable: true, maxWidth: 180 }),
        col('Description', 'Description', { maxWidth: 160 }),
        col('HrsQuoted', 'Quoted'),
        col('HrsWorked', 'Worked'),
        col('HrsInvoiced', 'Invoiced', { editable: true }),
        col('isPartiallyInvoiced', 'Partial'),
        col('Notes', 'Notes', { class: 'text-left', editable: true, maxWidth: 180 }),
        col('Approver', 'Approver', { class: 'text-left', editable: true, maxWidth: 180 }),
      ],

      headerTips: {
        Dr: 'Debit amount charged to the charge string / PO. Labor = labor rate × invoiced hours.',
        HrsWorked: 'Billable hours logged on the timesheet for this part.',
        HrsInvoiced: 'Hours being billed now. Labor Dr is recalculated when you change this.',
        isPartiallyInvoiced: 'Bill part of the worked hours now; the rest appears in the next billing cycle.',
      },
    };
  },

  computed: {
    // The Approval list is not filtered by date on the server.
    usesDates() {
      return this.currentRoute !== 'billing-approval' && !this.showingDismissed;
    },
    showingDismissed() {
      return this.currentRoute === 'billing-history' && this.historyView === 'dismissed';
    },
    canDismiss() {
      return ['billing-create', 'billing-confirmation'].includes(this.currentRoute);
    },
    dateRange() {
      return `${this.startDate}|${this.endDate}`;
    },
    startConfig() {
      return { dateFormat: 'Y-m-d', locale: { firstDayOfWeek: 1 }, maxDate: this.endDate || null };
    },
    endConfig() {
      return { dateFormat: 'Y-m-d', locale: { firstDayOfWeek: 1 }, minDate: this.startDate || null };
    },
    cycleMonths() {
      const today = new Date();
      return MONTHS.map((name, index) => {
        const [start, end] = this.monthRange(this.cycleYear, index);
        return {
          name,
          index,
          future: new Date(this.cycleYear, index, 1) > today,
          active: this.startDate === start && this.endDate === end,
        };
      });
    },
    action() {
      return this.showingDismissed ? RESTORE_ACTION : ACTIONS[this.currentRoute] || null;
    },
    // Cells can be edited on Create, Approval and Finalize only.
    editable() {
      return !!ACTIONS[this.currentRoute];
    },
    baseFields() {
      if (this.showingDismissed) return this.dismissedFields;
      return this.activeTab === 'internal' ? this.internalFields : this.externalFields;
    },
    tableFields() {
      return this.action ? [{ key: 'selected', label: '', class: 'text-center select-col' }, ...this.baseFields] : this.baseFields;
    },
    // Columns rendered through the generic text/edit slot
    textFields() {
      return this.baseFields.filter((f) => !['Project', 'Dr', 'HrsWorked', 'HrsQuoted', 'isPartiallyInvoiced', 'DismissedAt', 'PreviousStatus'].includes(f.key));
    },
    selectedRows() {
      return this.invoiceData.filter((row) => row.selected);
    },
    selectedTotal() {
      return this.sum(this.selectedRows, 'Dr');
    },
    allSelected() {
      return this.invoiceData.length > 0 && this.selectedRows.length === this.invoiceData.length;
    },
    someSelected() {
      return this.selectedRows.length > 0 && !this.allSelected;
    },
    totals() {
      return {
        Dr: this.sum(this.invoiceData, 'Dr'),
        HrsQuoted: this.sum(this.invoiceData, 'HrsQuoted'),
        HrsWorked: this.sum(this.invoiceData, 'HrsWorked'),
        HrsInvoiced: this.sum(this.invoiceData, 'HrsInvoiced'),
      };
    },
  },

  watch: {
    // The four billing routes share this component instance, so reload when the route changes.
    $route(to) {
      this.currentRoute = to.name;
      this.getInvoiceData();
    },
    // One watcher for both dates, so picking a billing cycle loads once.
    dateRange(value, old) {
      this.onDateChange(value, old);
    },
  },

  async beforeRouteLeave(to, from, next) {
    next(await this.confirmDiscard());
  },

  methods: {
    sum(rows, key) {
      return rows.reduce((total, row) => total + (parseFloat(row[key]) || 0), 0);
    },

    isLabor(item) {
      return (item.Description || '').includes('LABOR');
    },

    isOverQuote(item) {
      if (item.OverQuote !== undefined) return item.OverQuote;
      return this.isLabor(item) && parseFloat(item.HrsWorked) > parseFloat(item.HrsQuoted);
    },

    canEdit(item, key) {
      const field = this.baseFields.find((f) => f.key === key);
      if (!this.editable || !field || !field.editable) return false;
      if (key === 'HrsInvoiced') return this.isLabor(item);
      return true;
    },

    rowClass(item) {
      return item && item.selected ? 'row-selected' : '';
    },

    formatPrice(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(value) || 0);
    },

    formatHours(value) {
      const n = parseFloat(value) || 0;
      return Number.isInteger(n) ? String(n) : n.toFixed(2);
    },

    displayValue(value, key) {
      if (key === 'HrsInvoiced') return this.formatHours(value);
      return value === null || value === undefined || value === '' ? '—' : value;
    },

    isLong(value, field) {
      return value !== null && value !== undefined && String(value).length * 6.5 > (field.maxWidth || 160);
    },

    toast(title, text, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, text, icon: variant === 'success' ? 'ThumbsUpIcon' : 'AlertTriangleIcon', variant },
      });
    },

    notifyError(error, text) {
      console.error(error);
      this.toast('Something went wrong', text, 'danger');
    },

    async confirmDiscard() {
      if (!this.dirty) return true;
      const result = await this.$swal({
        title: 'Discard unsaved changes?',
        text: 'You have edited rows that are not saved yet.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard changes',
        cancelButtonText: 'Keep editing',
      });
      return result.isConfirmed;
    },

    // Shows how many records the other tab has, so it is not overlooked.
    async countOtherTab(loader) {
      const other = this.activeTab === 'internal' ? 'external' : 'internal';
      const route = this.currentRoute;
      this.tabCounts[other] = null;
      try {
        const response = await store.dispatch(loader, { type: other, startDate: this.startDate, endDate: this.endDate });
        if (route === this.currentRoute) this.tabCounts[other] = (response.data.items || []).length;
      } catch (error) {
        console.error(error);
      }
    },

    async switchTab(tab) {
      if (tab === this.activeTab || !(await this.confirmDiscard())) return;
      this.activeTab = tab;
      this.getInvoiceData();
    },

    async onDateChange(value, old) {
      if (this.revertingDate) {
        this.revertingDate = false;
        return;
      }
      if (!this.startDate || !this.endDate) return;
      if (!(await this.confirmDiscard())) {
        this.revertingDate = true;
        [this.startDate, this.endDate] = old.split('|');
        return;
      }
      this.getInvoiceData();
    },

    switchHistoryView(view) {
      if (view === this.historyView) return;
      this.historyView = view;
      this.getInvoiceData();
    },

    formatDateTime(value) {
      return value ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
    },

    rejectionTip(item) {
      const when = item.RejectedAt ? ` on ${this.formatDateTime(item.RejectedAt)}` : '';
      const reason = item.RejectReason ? `: ${item.RejectReason}` : '';
      return `Rejected at Approval by ${item.RejectedBy || 'someone'}${when}${reason}`;
    },

    async runReject() {
      const rows = this.selectedRows;
      const result = await this.$swal({
        title: `Reject ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}?`,
        html: `Total: <b>${this.formatPrice(this.sum(rows, 'Dr'))}</b><br><small>They go back to Billing &rarr; Create, marked as rejected, where they can be corrected and saved again.</small>`,
        icon: 'question',
        input: 'textarea',
        inputPlaceholder: 'Reason (optional), e.g. wrong charge string, hours need checking',
        showCancelButton: true,
        confirmButtonText: 'Reject',
        confirmButtonColor: '#ff9f43',
      });
      if (!result.isConfirmed) return;

      this.formShow = true;
      try {
        await store.dispatch('billing/rejectBilling', { data: rows.map((row) => ({ id: row.id })), reason: result.value });
        this.toast('Sent back to Create', `${rows.length} ${rows.length === 1 ? 'row was' : 'rows were'} rejected.`);
        this.dirty = false;
        await this.getInvoiceData();
      } catch (error) {
        this.formShow = false;
        this.notifyError(error, 'The rows could not be rejected.');
      }
    },

    async runDismiss() {
      const rows = this.selectedRows;
      const result = await this.$swal({
        title: `Dismiss ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}?`,
        html: `Total: <b>${this.formatPrice(this.sum(rows, 'Dr'))}</b><br><small>They will no longer show as pending. You can restore them from Billing &rarr; History &rarr; Dismissed.</small>`,
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: 'Reason (optional), e.g. test project, billed outside the system',
        showCancelButton: true,
        confirmButtonText: 'Dismiss',
        confirmButtonColor: '#ea5455',
      });
      if (!result.isConfirmed) return;

      this.formShow = true;
      try {
        const data = rows.map(({ selected, editing, ...row }) => row);
        await store.dispatch('billing/dismissBilling', { data, type: this.activeTab, stage: this.currentRoute, reason: result.value });
        this.toast('Dismissed', `${rows.length} ${rows.length === 1 ? 'row was' : 'rows were'} dismissed.`);
        this.dirty = false;
        await this.getInvoiceData();
      } catch (error) {
        this.formShow = false;
        this.notifyError(error, 'The rows could not be dismissed.');
      }
    },

    monthRange(year, month) {
      return [ymd(new Date(year, month, 1)), ymd(new Date(year, month + 1, 0))];
    },

    selectCycle(month) {
      [this.startDate, this.endDate] = this.monthRange(this.cycleYear, month);
    },

    formatDate(value) {
      return value ? parseYmd(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    },

    showPending() {
      const start = parseYmd(this.startDate);
      [this.startDate, this.endDate] = [this.pending.from, ymd(new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1))];
    },

    async getPending() {
      this.pending = { count: 0, from: null, to: null };
      if (!this.usesDates || this.currentRoute === 'billing-history') return;
      try {
        const response = await store.dispatch('billing/getPendingBilling', {
          type: this.activeTab,
          stage: this.currentRoute,
          startDate: this.startDate,
        });
        this.pending = response.data;
      } catch (error) {
        console.error(error);
      }
    },

    toggleAll(checked) {
      this.invoiceData.forEach((row) => this.$set(row, 'selected', checked));
    },

    async getInvoiceData() {
      this.formShow = true;
      this.cancelEditing();
      this.getPending();
      try {
        const loader = this.showingDismissed ? 'billing/getDismissedBilling' : LOADERS[this.currentRoute] || LOADERS['billing-create'];
        const response = await store.dispatch(loader, {
          type: this.activeTab,
          startDate: this.startDate,
          endDate: this.endDate,
        });
        this.invoiceData = (response.data.items || []).map((row) => ({ ...row, selected: false }));
        this.tabCounts[this.activeTab] = this.invoiceData.length;
        this.dirty = false;
        this.countOtherTab(loader);
      } catch (error) {
        this.notifyError(error, 'Billing records could not be loaded.');
      } finally {
        this.formShow = false;
      }
    },

    startEditing(item, key) {
      if (!this.canEdit(item, key)) return;
      this.editingItem = item;
      this.editingCell = key;
      this.editValue = item[key];
    },

    cancelEditing() {
      this.editingItem = null;
      this.editingCell = null;
      this.editValue = null;
    },

    endEditing(item, key) {
      if (this.editingItem !== item || this.editingCell !== key) return;
      const value = this.editValue;
      this.cancelEditing();

      if (key === 'HrsInvoiced') return this.updateInvoicedHours(item, value);
      if ((item[key] ?? '') === (value ?? '')) return;

      if ((this.activeTab === 'internal' && CHARGE_FIELDS.includes(key)) || (this.activeTab === 'external' && key === 'PoNumber')) {
        return this.updateChargeString(item, key, value);
      }

      item[key] = value;
      this.dirty = true;
    },

    updateInvoicedHours(item, value) {
      const hours = parseFloat(value);
      const worked = parseFloat(item.HrsWorked) || 0;

      if (Number.isNaN(hours) || hours < 0 || hours > worked) {
        this.toast('Invalid hours', `Invoiced hours must be between 0 and ${this.formatHours(worked)} (hours worked).`, 'warning');
        return;
      }
      if (hours === parseFloat(item.HrsInvoiced)) return;

      item.HrsInvoiced = hours;
      if (parseFloat(item.LaborRate) > 0) {
        item.Dr = Math.round(item.LaborRate * hours * 100) / 100;
      }
      this.dirty = true;

      if (hours > 0 && hours < worked) {
        this.$swal({
          title: 'Partial Invoice',
          text: `You are invoicing ${this.formatHours(hours)} hours but ${this.formatHours(worked)} hours were worked. Is this a partial invoice?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes, Partial Invoice',
          cancelButtonText: 'No',
          confirmButtonColor: '#28a745',
        }).then((result) => {
          this.$set(item, 'isPartiallyInvoiced', result.isConfirmed);
          this.$set(item, 'HrsRemaining', result.isConfirmed ? worked - hours : 0);
          if (result.isConfirmed) {
            this.toast('Partial Invoice', `Remaining ${this.formatHours(item.HrsRemaining)} hours will appear in the next billing cycle.`);
          }
        });
      } else {
        this.$set(item, 'isPartiallyInvoiced', false);
        this.$set(item, 'HrsRemaining', 0);
      }
    },

    onPartialCheckboxChange(item) {
      const worked = parseFloat(item.HrsWorked) || 0;
      const invoiced = parseFloat(item.HrsInvoiced) || 0;
      this.$set(item, 'HrsRemaining', item.isPartiallyInvoiced ? Math.max(0, worked - invoiced) : 0);
      this.dirty = true;
    },

    // Charge strings live on the project, so the change is saved right away
    // and applied to every row of that project without reloading the table.
    async updateChargeString(item, key, value) {
      const parts = { ...item, [key]: value };
      const chargeNew = this.activeTab === 'internal' ? CHARGE_FIELDS.map((f) => parts[f] || '').join('') : value || '';

      try {
        await store.dispatch('billing/updateChargeString', { id: item.Project, chargeNew });
        this.invoiceData
          .filter((row) => row.Project === item.Project)
          .forEach((row) => { row[key] = value; });
        this.toast('Saved', `Charge string updated for project ${item.Project}.`);
      } catch (error) {
        this.notifyError(error, 'The charge string could not be saved.');
      }
    },

    async runAction() {
      const action = this.action;
      let rows = this.selectedRows;

      if (action.verb === 'save') {
        const missing = rows.filter((row) => !row.Approver);
        if (missing.length) {
          const projects = [...new Set(missing.map((row) => row.Project))].join(', ');
          rows = rows.filter((row) => row.Approver);
          const result = await this.$swal({
            title: 'Missing approver',
            html: `${missing.length} selected row(s) have no approver and will not be saved.<br><small>Projects: ${projects}</small>`,
            icon: 'warning',
            showCancelButton: rows.length > 0,
            showConfirmButton: rows.length > 0,
            confirmButtonText: `Save the other ${rows.length}`,
            cancelButtonText: rows.length ? 'Cancel' : 'OK',
          });
          if (!result.isConfirmed || !rows.length) return;
        }
      }

      const total = this.formatPrice(this.sum(rows, 'Dr'));
      const confirm = await this.$swal({
        title: `${action.label} ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}?`,
        html: `Total: <b>${total}</b>${action.verb === 'finalize' ? '<br>Finalizing creates the invoices and cannot be undone.' : ''}${action.verb === 'restore' ? '<br>They will show as pending again.' : ''}`,
        icon: action.verb === 'finalize' ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonText: action.label,
      });
      if (!confirm.isConfirmed) return;

      this.formShow = true;
      try {
        const data = rows.map(({ selected, editing, ...row }) => row);
        await store.dispatch(action.store, { data, type: this.activeTab });
        this.toast('Successful', `✔️ ${rows.length} ${rows.length === 1 ? 'row has' : 'rows have'} been ${action.done}.`);
        this.dirty = false;
        await this.getInvoiceData();
      } catch (error) {
        this.formShow = false;
        this.notifyError(error, `The rows could not be ${action.done}.`);
      }
    },

    async exportExcel() {
      this.formShow = true;
      try {
        const res = await store.dispatch('billing/exportExcel', { data: this.invoiceData, type: this.activeTab });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.endDate}-billing.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        this.notifyError(error, 'The export could not be created.');
      } finally {
        this.formShow = false;
      }
    },
  },

  mounted() {
    this.currentRoute = router.currentRoute.name;
    this.getInvoiceData();
  },

  destroyed() {
    this.isNavMenuHidden = false;
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
.billing-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: #f3f2f7;
}

.billing-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 210px;
  padding: 10px 14px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: #5e5873;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
}

.billing-tab:hover:not(.active) {
  background: rgba(255, 255, 255, 0.7);
}

.billing-tab.active {
  background: #a51c30;
  color: #fff;
  box-shadow: 0 4px 12px rgba(165, 28, 48, 0.35);
}

.billing-tab:focus-visible {
  outline: 2px solid #a51c30;
  outline-offset: 2px;
}

.billing-tab-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #fff;
  color: #a51c30;
  flex: none;
}

.billing-tab.active .billing-tab-icon {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.billing-tab-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.billing-tab-label {
  font-size: 15px;
  font-weight: 700;
}

.billing-tab-sub {
  font-size: 11px;
  opacity: 0.75;
}

.billing-tab-count {
  margin-left: auto;
  min-width: 26px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e4e2ec;
  color: #5e5873;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.billing-tab.active .billing-tab-count {
  background: #fff;
  color: #a51c30;
}

.billing-tab-count.attention {
  background: #a51c30;
  color: #fff;
  animation: count-pulse 2s ease-in-out 2;
}

@keyframes count-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(165, 28, 48, 0.45); }
  50% { box-shadow: 0 0 0 6px rgba(165, 28, 48, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .billing-tab-count.attention { animation: none; }
}

.billing-hint {
  font-size: 12px;
  color: #6e6b7b;
}

.history-switch {
  display: inline-flex;
  padding: 3px;
  border-radius: 999px;
  background: #f3f2f7;
}

.history-switch button {
  border: none;
  background: none;
  border-radius: 999px;
  padding: 4px 16px;
  font-size: 12px;
  font-weight: 500;
  color: #6e6b7b;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.history-switch button.active {
  background: #fff;
  color: #a51c30;
  box-shadow: 0 1px 3px rgba(34, 41, 47, 0.15);
}

.history-switch button:focus-visible {
  outline: 2px solid #a51c30;
  outline-offset: 2px;
}

.pending-alert .alert-body {
  gap: 4px;
}

.billing-cycles {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.billing-cycles-label {
  font-size: 12px;
  font-weight: 500;
  color: #5e5873;
}

.cycle-year {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-size: 13px;
  color: #5e5873;
}

.cycle-year-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #ebe9f1;
  border-radius: 50%;
  background: #fff;
  color: #6e6b7b;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
}

.cycle-year-btn:hover:not(:disabled) {
  background: rgba(165, 28, 48, 0.12);
  color: #a51c30;
}

.cycle-year-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.cycle-months {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cycle-chip {
  border: 1px solid #ebe9f1;
  background: #fff;
  color: #6e6b7b;
  border-radius: 999px;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
}

.cycle-chip:hover:not(:disabled):not(.active) {
  border-color: #a51c30;
  color: #a51c30;
  background: rgba(165, 28, 48, 0.06);
}

.cycle-chip.active {
  background: #a51c30;
  border-color: #a51c30;
  color: #fff;
  box-shadow: 0 2px 6px rgba(165, 28, 48, 0.4);
}

.cycle-chip:disabled {
  opacity: 0.4;
  cursor: default;
}

.cycle-chip:focus-visible,
.cycle-year-btn:focus-visible {
  outline: 2px solid #a51c30;
  outline-offset: 2px;
}

.billing-table-wrap {
  overflow-x: auto;
}

.th-help {
  cursor: help;
  white-space: nowrap;
}

.cell-text {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.editable-cell {
  cursor: text;
  border-bottom: 1px dashed #b9b9c3;
  min-height: 18px;
  position: relative;
}

.editable-cell:hover {
  background-color: rgba(165, 28, 48, 0.08);
}

.rejected-tag {
  display: inline-block;
  margin-left: 4px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(255, 159, 67, 0.16);
  color: #c46a12;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  cursor: help;
  white-space: nowrap;
}

.over-quote {
  color: #ea5455;
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
}

::v-deep .table > tbody > tr > td,
::v-deep .table > tfoot > tr > td {
  font-size: 12px !important;
  vertical-align: middle;
}

::v-deep .row-selected > td {
  background-color: rgba(165, 28, 48, 0.06);
}

::v-deep .totals-row > td {
  font-weight: 700;
  border-top: 2px solid #ebe9f1;
  white-space: nowrap;
}

::v-deep .select-col {
  width: 32px;
}
</style>
